import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import AppIcon from './icons/AppIcon';
import 'leaflet/dist/leaflet.css';

// Correction des icônes par défaut de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function MapLocationPicker({ 
  latitude, 
  longitude, 
  address, 
  onLocationChange,
  isExpanded
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  // Initialiser la carte
  useEffect(() => {
    if (!isExpanded || !mapContainer.current) return;

    // Éviter la réinitialisation si la carte existe déjà
    if (map.current) return;

    const lat = latitude || 48.8566; // Paris par défaut
    const lng = longitude || 2.3522;

    // Créer la carte
    map.current = L.map(mapContainer.current).setView([lat, lng], 13);

    // Ajouter les tuiles OpenStreetMap
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
    }).addTo(map.current);

    // Créer le marqueur
    marker.current = L.marker([lat, lng], {
      draggable: true,
      title: 'Déplacez le marqueur pour définir le lieu'
    }).addTo(map.current);

    // Événement de déplacement du marqueur
    marker.current.on('dragend', () => {
      const pos = marker.current.getLatLng();
      onLocationChange({
        latitude: pos.lat,
        longitude: pos.lng
      });
      // Reverse geocoding
      fetchAddress(pos.lat, pos.lng);
    });

    // Fix pour le focus dans la map
    map.current.on('focus', () => {
      map.current.dragging.enable();
    });

    return () => {
      // Cleanup pas nécessaire ici car on ne recrée pas la map
    };
  }, [isExpanded]);

  // Mettre à jour la position du marqueur si latitude/longitude changent
  useEffect(() => {
    if (!map.current || !marker.current || !isExpanded) return;
    
    const lat = latitude || 48.8566;
    const lng = longitude || 2.3522;

    marker.current.setLatLng([lat, lng]);
    map.current.setView([lat, lng], 13);
  }, [latitude, longitude, isExpanded]);

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      if (data.address) {
        const addressStr = data.address.road || '';
        const city = data.address.city || data.address.town || data.address.village || '';
        const postalCode = data.address.postcode || '';
        const fullAddress = [addressStr, postalCode, city]
          .filter(Boolean)
          .join(' ')
          .trim();
        
        if (fullAddress) {
          onLocationChange({
            latitude: lat,
            longitude: lng,
            address: fullAddress
          });
        }
      }
    } catch (err) {
      console.error('Erreur lors du reverse geocoding:', err);
    }
  };

  const handleUseMyPosition = () => {
    setIsGettingLocation(true);
    setLocationError('');

    if (!navigator.geolocation) {
      setLocationError('La géolocalisation n\'est pas supportée par votre navigateur');
      setIsGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        onLocationChange({ latitude, longitude });
        fetchAddress(latitude, longitude);
        
        // Mettre à jour la vue de la carte
        if (map.current && marker.current) {
          marker.current.setLatLng([latitude, longitude]);
          map.current.setView([latitude, longitude], 13);
        }
        
        setIsGettingLocation(false);
      },
      (error) => {
        let errorMsg = 'Erreur de géolocalisation';
        if (error.code === error.PERMISSION_DENIED) {
          errorMsg = 'Permission de géolocalisation refusée';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMsg = 'Position indisponible';
        } else if (error.code === error.TIMEOUT) {
          errorMsg = 'Délai de géolocalisation dépassé';
        }
        setLocationError(errorMsg);
        setIsGettingLocation(false);
      }
    );
  };

  return (
    <div className="animate-fadeInMap">
      <style>{`
        @keyframes fadeInMap {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInMap {
          animation: fadeInMap 0.3s ease-in;
        }
        .map-location-btn {
          width: 100%;
          margin-bottom: 12px;
          padding: 12px 24px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          font-weight: 600;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 1rem;
        }
        .map-location-btn:hover:not(:disabled) {
          transform: scale(1.02);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
        }
        .map-location-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .location-error {
          margin-bottom: 12px;
          padding: 12px;
          background: rgba(239, 68, 68, 0.05);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .location-error p {
          font-size: 0.875rem;
          color: #dc2626;
          margin: 0;
          font-weight: 500;
        }
        .map-container {
          position: relative;
          height: 400px;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-bottom: 12px;
          background: #f3f4f6;
          transition: all 0.3s ease;
        }
        .map-loading-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(4px);
          z-index: 10;
        }
        .map-loading-overlay .spinner {
          font-size: 2rem;
          margin-bottom: 12px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .coordinates-display {
          padding: 12px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(34, 197, 234, 0.05) 100%);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 8px;
        }
        .coordinates-display .label {
          font-size: 0.75rem;
          color: #2563eb;
          font-weight: 600;
          margin-bottom: 6px;
        }
        .coordinates-display .value {
          font-family: 'Courier New', monospace;
          font-size: 0.875rem;
          color: #1e40af;
        }
        .leaflet-container {
          font-family: inherit;
        }
        .leaflet-control-attribution {
          font-size: 0.7rem !important;
        }
      `}</style>

      {/* Location Button */}
      <button
        type="button"
        onClick={handleUseMyPosition}
        disabled={isGettingLocation}
        className="map-location-btn"
      >
        <AppIcon name="location" size={18} />
        {isGettingLocation ? 'Localisation en cours...' : 'Utiliser ma position'}
      </button>

      {/* Error Message */}
      {locationError && (
        <div className="location-error">
          <AppIcon name="error" size={18} />
          <p>{locationError}</p>
        </div>
      )}

      {/* Map Container */}
      <div 
        ref={mapContainer} 
        className="map-container"
      >
        {isGettingLocation && (
          <div className="map-loading-overlay">
            <div className="spinner"><AppIcon name="search" size={22} /></div>
            <p style={{color: '#4b5563', fontWeight: '500'}}>Localisation en cours...</p>
          </div>
        )}
      </div>

      {/* Coordinates Display */}
      {latitude && longitude && (
        <div className="coordinates-display">
          <div className="label"><AppIcon name="location" size={16} /> Coordonnées GPS</div>
          <div className="value">
            {latitude.toFixed(6)}° N, {longitude.toFixed(6)}° E
          </div>
        </div>
      )}
    </div>
  );
}
